import React from "react";

import { faRobot } from "@fortawesome/pro-regular-svg-icons";

import { ExecutionLogView } from "src/agents/AgentVersionPage/AgentPlayground";
import { SessionActions } from "src/agents/base/SessionActions";
import {
  useAgentSession,
  useSessionToPlaygroundState,
} from "src/agents/queries/execution";
import type {
  AgentNode,
  NodeHistoricalRunStateV2,
} from "src/constants/NodeDataTypes";
import { EmptyState } from "src/design-system/EmptyState";

type AgentTraceTabProps = {
  node: AgentNode;
  runState: NodeHistoricalRunStateV2;
};

export const AgentTraceTab: React.FC<AgentTraceTabProps> = ({
  node,
  runState,
}) => {
  const nodeMetadata = runState.nodeExecutionMetadata?.[node.id];
  const sessionId = nodeMetadata?.agent_session_id ?? null;
  const isMockData = nodeMetadata?.data_source === "mock_data";
  const sessionQuery = useAgentSession(isMockData ? null : sessionId, {
    polling: true,
  });
  const sessionState = useSessionToPlaygroundState(
    sessionQuery.data,
    isMockData ? null : sessionId,
  );

  if (!sessionId || isMockData) {
    return (
      <EmptyState
        description="Agent trace is not available for this decision."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="p-4 font-inter-normal-13px text-gray-500">
        Loading agent trace...
      </div>
    );
  }

  if (
    sessionQuery.isError ||
    !sessionQuery.data ||
    !sessionState ||
    sessionState.status === "idle"
  ) {
    return (
      <EmptyState
        description="Agent trace could not be loaded."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  return (
    <div className="flex h-full flex-col px-5">
      <div className="flex justify-end pt-2 pr-3">
        <SessionActions
          rawSessionData={sessionQuery.data ?? null}
          sessionId={sessionId}
        />
      </div>
      <ExecutionLogView
        key={sessionState.sessionId}
        hideTestInput={false}
        inputTitle="User input"
        sessionState={sessionState}
      />
    </div>
  );
};
